import { useState, useEffect } from "react";
import adminApi from "../api/adminAxios";

const inr = n => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
const fmt  = d => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const REPORT_TYPES = [
  { id: "sales",    label: "Sales Report",    icon: "💰", desc: "Revenue, orders & payment breakdown" },
  { id: "products", label: "Products Report", icon: "🕯️", desc: "Stock levels & top sellers" },
  { id: "users",    label: "Customers Report", icon: "👥", desc: "Registrations & order activity" },
  { id: "orders",   label: "Orders Report",   icon: "📦", desc: "Order status & fulfilment" },
  { id: "monthly",  label: "Monthly Report",  icon: "📅", desc: "Month-wise revenue & order trends" },
];

export default function ManageReports() {
  const [activeReport, setActiveReport] = useState("sales");
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  useEffect(() => { loadReport(activeReport); }, [activeReport]);

  const loadReport = async (type) => {
    setLoading(true); setError(""); setData(null);
    try {
      const [statsRes, ordersRes, productsRes, usersRes] = await Promise.all([
        adminApi.get("admin/stats/"),
        adminApi.get("admin/orders/"),
        adminApi.get("admin/products/"),
        adminApi.get("admin/users/"),
      ]);
      setData({ stats: statsRes.data, orders: ordersRes.data, products: productsRes.data, users: usersRes.data });
    } catch {
      setError("Failed to load report data. Please try again.");
    } finally { setLoading(false); }
  };

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    if (!data) return;
    let csv = "";
    let filename = "report.csv";

    if (activeReport === "sales") {
      filename = "sales_report.csv";
      csv = "Order ID,Date,Customer,Amount,Payment,Status\n";
      data.orders.forEach(o => {
        csv += `${o.id},"${fmt(o.created_at)}","${o.user}",${o.total_price},"${o.payment_method}","${o.status}"\n`;
      });
    } else if (activeReport === "products") {
      filename = "products_report.csv";
      csv = "ID,Name,Category,Price,Stock\n";
      data.products.forEach(p => {
        csv += `${p.id},"${p.name}","${typeof p.category === "object" ? p.category?.name : p.category}",${p.price},${p.stock_quantity}\n`;
      });
    } else if (activeReport === "users") {
      filename = "customers_report.csv";
      csv = "ID,Username,Email,Contact,Role,Orders,Joined\n";
      data.users.forEach(u => {
        csv += `${u.id},"${u.username}","${u.email}","${u.contact_number || ""}","${u.role}",${u.order_count},"${fmt(u.date_joined)}"\n`;
      });
    } else if (activeReport === "orders") {
      filename = "orders_report.csv";
      csv = "Order ID,Date,Customer,Amount,Payment,Status,Address\n";
      data.orders.forEach(o => {
        csv += `${o.id},"${fmt(o.created_at)}","${o.user}",${o.total_price},"${o.payment_method}","${o.status}","${o.shipping_address}"\n`;
      });
    }

    const blob  = new Blob([csv], { type: "text/csv" });
    const url   = URL.createObjectURL(blob);
    const a     = document.createElement("a");
    a.href      = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "32px 36px", fontFamily: "Georgia, serif" }} className="report-page">

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
        <div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>Reports</h1>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa", marginTop: "4px" }}>Generate and export business reports.</p>
        </div>
        <div style={{ display: "flex", gap: "10px" }} className="no-print">
          <button onClick={handleExportCSV} disabled={!data || loading}
            style={{ padding: "10px 18px", borderRadius: "10px", border: "1.5px solid #e8d5b8", background: "#fff", color: "#888", cursor: !data || loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
            ⬇ Export CSV
          </button>
          <button onClick={handlePrint} disabled={!data || loading}
            style={{ padding: "10px 18px", borderRadius: "10px", background: !data || loading ? "#e0a870" : "linear-gradient(135deg, #e07b39, #c96a18)", color: "#fff", border: "none", cursor: !data || loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 4px 14px rgba(201,106,24,.25)" }}>
            🖨 Print Report
          </button>
        </div>
      </div>

      {/* Report type selector */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "12px", marginBottom: "24px" }} className="no-print report-type-grid">
        {REPORT_TYPES.map(({ id, label, icon, desc }) => (
          <button key={id} onClick={() => setActiveReport(id)}
            style={{ padding: "16px", borderRadius: "14px", border: `2px solid ${activeReport === id ? "#e07b39" : "#f0ebe3"}`, background: activeReport === id ? "#fff4ec" : "#fff", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}>
            <div style={{ fontSize: "22px", marginBottom: "8px" }}>{icon}</div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: activeReport === id ? "#e07b39" : "#1a1a1a", marginBottom: "3px" }}>{label}</p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa" }}>{desc}</p>
          </button>
        ))}
      </div>

      {/* Report title for print */}
      <div className="print-only" style={{ display: "none", marginBottom: "20px" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color: "#1a1a1a" }}>
          SJ Aroma Candles — {REPORT_TYPES.find(r => r.id === activeReport)?.label}
        </h2>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa" }}>Generated on {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: "#fff0f0", border: "1px solid #f5c6cb", borderRadius: "10px", padding: "14px 18px", marginBottom: "20px", fontFamily: "Georgia, serif", fontSize: "12px", color: "#c0392b" }}>
          ⚠ {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", padding: "32px", textAlign: "center" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>Generating report...</p>
        </div>
      )}

      {/* ── SALES REPORT ─────────────────────────────────────────────────── */}
      {!loading && data && activeReport === "sales" && <SalesReport data={data} />}

      {/* ── PRODUCTS REPORT ──────────────────────────────────────────────── */}
      {!loading && data && activeReport === "products" && <ProductsReport data={data} />}

      {/* ── USERS REPORT ─────────────────────────────────────────────────── */}
      {!loading && data && activeReport === "users" && <UsersReport data={data} />}

      {/* ── ORDERS REPORT ────────────────────────────────────────────────── */}
      {!loading && data && activeReport === "orders" && <OrdersReport data={data} />}

      {/* ── MONTHLY REPORT ────────────────────────────────────────────────── */}
      {!loading && data && activeReport === "monthly" && <MonthlyReport data={data} />}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .report-page { padding: 16px !important; }
          body { background: #fff; }
        }
        @media (max-width: 1100px) {
          .report-type-grid { grid-template-columns: repeat(3,1fr) !important; }
        }
        @media (max-width: 700px) {
          .report-type-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
      `}</style>
    </div>
  );
}

// ── SALES REPORT ──────────────────────────────────────────────────────────────
function SalesReport({ data }) {
  const { stats, orders } = data;
  // Only count delivered orders for revenue — processing/shipped not yet fulfilled
  const deliveredOrders = orders.filter(o => o.status === "delivered");
  const totalRevenue = deliveredOrders.reduce((s, o) => s + parseFloat(o.total_price || 0), 0);

  const byPayment = deliveredOrders.reduce((acc, o) => {
    const pm = o.payment_method || "unknown";
    acc[pm] = (acc[pm] || 0) + parseFloat(o.total_price || 0);
    return acc;
  }, {});

  const byStatus = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  const STATUS_COLORS = { pending: "#e07b39", processing: "#c4a020", shipped: "#3a6ab0", delivered: "#3a8a68", cancelled: "#c0392b" };

  return (
    <div>
      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Revenue (Delivered)", value: inr(totalRevenue),   icon: "💰", color: "#3a8a68" },
          { label: "Total Orders",   value: stats.total_orders,       icon: "📦", color: "#e07b39" },
          { label: "Delivered",      value: stats.delivered_orders,   icon: "✅", color: "#3a8a68" },
          { label: "Pending",        value: stats.pending_orders,     icon: "⏳", color: "#c4a020" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>{icon}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
            </div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
        {/* Revenue by payment method */}
        <div style={{ background: "#fff", borderRadius: "14px", padding: "22px", border: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a", marginBottom: "4px" }}>Revenue by Payment Method</p>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa", marginBottom: "14px" }}>Delivered orders only</p>
          {Object.entries(byPayment).map(([method, amount]) => {
            const pct = totalRevenue > 0 ? Math.round((amount / totalRevenue) * 100) : 0;
            return (
              <div key={method} style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#555", textTransform: "capitalize" }}>{method}</span>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a" }}>{inr(amount)} <span style={{ color: "#aaa", fontWeight: "400" }}>({pct}%)</span></span>
                </div>
                <div style={{ height: "6px", borderRadius: "999px", background: "#f0ebe3", overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, #e07b39, #c96a18)", borderRadius: "999px", transition: "width 0.6s ease" }} />
                </div>
              </div>
            );
          })}
          {Object.keys(byPayment).length === 0 && <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa" }}>No data available.</p>}
        </div>

        {/* Orders by status */}
        <div style={{ background: "#fff", borderRadius: "14px", padding: "22px", border: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a", marginBottom: "16px" }}>Orders by Status</p>
          {Object.entries(byStatus).map(([st, count]) => (
            <div key={st} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f7f2eb" }}>
              <span style={{ padding: "3px 10px", borderRadius: "999px", fontSize: "11px", fontWeight: "700", fontFamily: "Georgia, serif", color: STATUS_COLORS[st] || "#888", background: `${STATUS_COLORS[st]}18` || "#f5f5f5", textTransform: "capitalize" }}>{st}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>{count}</span>
            </div>
          ))}
          {Object.keys(byStatus).length === 0 && <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa" }}>No orders yet.</p>}
        </div>
      </div>

      {/* Recent orders table */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>All Orders ({orders.length})</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "70px 1fr 130px 100px 100px 100px", gap: "8px", padding: "10px 22px", background: "#f7f2eb" }}>
          {["ORDER", "CUSTOMER", "DATE", "AMOUNT", "PAYMENT", "STATUS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>
        {orders.slice(0, 20).map(o => (
          <div key={o.id} style={{ display: "grid", gridTemplateColumns: "70px 1fr 130px 100px 100px 100px", gap: "8px", padding: "12px 22px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#e07b39" }}>#{o.id}</span>
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>{o.user}</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa" }}>{o.user_email}</p>
            </div>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555" }}>{fmt(o.created_at)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a" }}>{inr(o.total_price)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#3a8a68", textTransform: "uppercase" }}>{o.payment_method || "—"}</span>
            <span style={{ padding: "2px 8px", borderRadius: "999px", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", color: STATUS_COLORS[o.status] || "#888", background: `${STATUS_COLORS[o.status]}18`, textTransform: "capitalize" }}>{o.status}</span>
          </div>
        ))}
        {orders.length === 0 && <p style={{ padding: "24px 22px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>No orders found.</p>}
      </div>
    </div>
  );
}

// ── PRODUCTS REPORT ───────────────────────────────────────────────────────────
function ProductsReport({ data }) {
  const { products, orders } = data;
  const totalStock   = products.reduce((s, p) => s + (p.stock_quantity || 0), 0);
  const outOfStock   = products.filter(p => p.stock_quantity === 0).length;
  const lowStock     = products.filter(p => p.stock_quantity > 0 && p.stock_quantity <= 5).length;

  const getCatName = p => typeof p.category === "object" ? p.category?.name || "—" : p.category || "—";

  // Compute most sold products from order items
  const salesMap = {};
  orders.forEach(o => {
    (o.items || []).forEach(item => {
      if (!item.product_name) return;
      const key = item.product_id || item.product_name;
      if (!salesMap[key]) salesMap[key] = { name: item.product_name, qty: 0, revenue: 0 };
      salesMap[key].qty     += item.quantity || 1;
      salesMap[key].revenue += parseFloat(item.price_at_purchase || 0) * (item.quantity || 1);
    });
  });
  const topSellers = Object.values(salesMap).sort((a, b) => b.qty - a.qty).slice(0, 8);
  const maxQty = topSellers[0]?.qty || 1;

  const MEDAL = ["🥇","🥈","🥉"];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Total Products", value: products.length, icon: "🕯️", color: "#e07b39" },
          { label: "Total Stock",    value: totalStock,       icon: "📦", color: "#3a6ab0" },
          { label: "Out of Stock",   value: outOfStock,       icon: "🚫", color: "#c0392b" },
          { label: "Low Stock (≤5)", value: lowStock,         icon: "⚠️", color: "#c4a020" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>{icon}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
            </div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* ── Most Sold / Popular Products ── */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", padding: "22px", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
          <span style={{ fontSize: "22px" }}>🏆</span>
          <div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#1a1a1a" }}>Most Sold Products</p>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>Ranked by total units sold across all orders</p>
          </div>
        </div>

        {topSellers.length === 0 ? (
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>
            No order item data available yet.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {topSellers.map((p, i) => {
              const pct = Math.round((p.qty / maxQty) * 100);
              return (
                <div key={p.name}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ fontSize: "18px", minWidth: "24px", textAlign: "center" }}>
                        {MEDAL[i] || `#${i + 1}`}
                      </span>
                      <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: i < 3 ? "700" : "600", color: i < 3 ? "#1a1a1a" : "#555" }}>
                        {p.name}
                      </span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#e07b39" }}>
                        {p.qty} units
                      </span>
                      <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", marginLeft: "8px" }}>
                        {inr(p.revenue)}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: "8px", borderRadius: "999px", background: "#f0ebe3", overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: "999px", transition: "width 0.6s ease",
                      background: i === 0 ? "linear-gradient(90deg,#e07b39,#c96a18)" : i === 1 ? "linear-gradient(90deg,#c4a020,#a08010)" : i === 2 ? "linear-gradient(90deg,#3a6ab0,#2a5a90)" : "#d4c4a8"
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>All Products ({products.length})</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "55px 1fr 120px 100px 80px 100px", gap: "8px", padding: "10px 22px", background: "#f7f2eb" }}>
          {["ID", "PRODUCT", "CATEGORY", "PRICE", "STOCK", "STATUS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>
        {products.map(p => (
          <div key={p.id} style={{ display: "grid", gridTemplateColumns: "55px 1fr 120px 100px 80px 100px", gap: "8px", padding: "12px 22px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>#{p.id}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {p.image
                ? <img src={p.image} alt={p.name} style={{ width: "32px", height: "32px", borderRadius: "6px", objectFit: "cover", flexShrink: 0 }} />
                : <div style={{ width: "32px", height: "32px", borderRadius: "6px", background: "#f0ebe3", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "14px", flexShrink: 0 }}>🕯️</div>}
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>{p.name}</span>
            </div>
            <span style={{ padding: "2px 10px", borderRadius: "999px", background: "#f0ebe3", fontFamily: "Georgia, serif", fontSize: "10px", color: "#7a5c3a", display: "inline-block", width: "fit-content" }}>{getCatName(p)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#e07b39" }}>{inr(p.price)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: p.stock_quantity === 0 ? "#c0392b" : p.stock_quantity <= 5 ? "#c4a020" : "#3a8a68" }}>{p.stock_quantity}</span>
            <span style={{ padding: "2px 8px", borderRadius: "999px", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", color: p.stock_quantity === 0 ? "#c0392b" : p.stock_quantity <= 5 ? "#c4a020" : "#3a8a68", background: p.stock_quantity === 0 ? "#fff0f0" : p.stock_quantity <= 5 ? "#fffbec" : "#eefaf4" }}>
              {p.stock_quantity === 0 ? "Out of Stock" : p.stock_quantity <= 5 ? "Low Stock" : "In Stock"}
            </span>
          </div>
        ))}
        {products.length === 0 && <p style={{ padding: "24px 22px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>No products found.</p>}
      </div>
    </div>
  );
}

// ── USERS REPORT ──────────────────────────────────────────────────────────────
function UsersReport({ data }) {
  const { users } = data;
  const active     = users.filter(u => u.is_active).length;
  const withOrders = users.filter(u => u.order_count > 0).length;
  const newThisWeek = users.filter(u => new Date(u.date_joined) > new Date(Date.now() - 7 * 86400000)).length;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Total Customers", value: users.length,   icon: "👥", color: "#e07b39" },
          { label: "Active",          value: active,          icon: "✅", color: "#3a8a68" },
          { label: "Have Orders",     value: withOrders,      icon: "📦", color: "#3a6ab0" },
          { label: "New This Week",   value: newThisWeek,     icon: "🆕", color: "#8a3ab0" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>{icon}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
            </div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>All Customers ({users.length})</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "70px 1fr 1fr 120px 60px 80px 100px", gap: "8px", padding: "10px 22px", background: "#f7f2eb" }}>
          {["ID", "USERNAME", "EMAIL", "CONTACT", "ORDERS", "STATUS", "JOINED"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>
        {users.map(u => (
          <div key={u.id} style={{ display: "grid", gridTemplateColumns: "70px 1fr 1fr 120px 60px 80px 100px", gap: "8px", padding: "12px 22px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#e07b39" }}>#{u.id}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>{u.username}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555" }}>{u.contact_number || "—"}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: u.order_count > 0 ? "#e07b39" : "#aaa", textAlign: "center" }}>{u.order_count}</span>
            <span style={{ padding: "2px 8px", borderRadius: "999px", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", color: u.is_active ? "#3a8a68" : "#c0392b", background: u.is_active ? "#eefaf4" : "#fff0f0" }}>{u.is_active ? "Active" : "Inactive"}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>{fmt(u.date_joined)}</span>
          </div>
        ))}
        {users.length === 0 && <p style={{ padding: "24px 22px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>No customers found.</p>}
      </div>
    </div>
  );
}

// ── ORDERS REPORT ─────────────────────────────────────────────────────────────
function OrdersReport({ data }) {
  const { orders } = data;
  const STATUS_COLORS = { pending: "#e07b39", processing: "#c4a020", shipped: "#3a6ab0", delivered: "#3a8a68", cancelled: "#c0392b" };

  const statusCounts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const deliveredOnly = orders.filter(o => o.status === "delivered");
  const avgOrder = deliveredOnly.length > 0 ? deliveredOnly.reduce((s, o) => s + parseFloat(o.total_price || 0), 0) / deliveredOnly.length : 0;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Total Orders",    value: orders.length,                     icon: "📦", color: "#e07b39" },
          { label: "Delivered",       value: statusCounts.delivered || 0,       icon: "✅", color: "#3a8a68" },
          { label: "Cancelled",       value: statusCounts.cancelled || 0,       icon: "🚫", color: "#c0392b" },
          { label: "Avg Order Value", value: inr(avgOrder),                     icon: "📊", color: "#3a6ab0" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>{icon}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
            </div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "22px", fontWeight: "700", color }}>{value}</p>
          </div>
        ))}
      </div>

      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>All Orders ({orders.length})</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "65px 130px 1fr 100px 100px 100px 1fr", gap: "8px", padding: "10px 22px", background: "#f7f2eb" }}>
          {["ORDER", "DATE", "CUSTOMER", "AMOUNT", "PAYMENT", "STATUS", "ADDRESS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>
        {orders.map(o => (
          <div key={o.id} style={{ display: "grid", gridTemplateColumns: "65px 130px 1fr 100px 100px 100px 1fr", gap: "8px", padding: "12px 22px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#e07b39" }}>#{o.id}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#555" }}>{fmt(o.created_at)}</span>
            <div>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a" }}>{o.user}</p>
              <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#aaa" }}>{o.user_email}</p>
            </div>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#1a1a1a" }}>{inr(o.total_price)}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#3a8a68", textTransform: "uppercase" }}>{o.payment_method || "—"}</span>
            <span style={{ padding: "2px 8px", borderRadius: "999px", fontSize: "10px", fontWeight: "700", fontFamily: "Georgia, serif", color: STATUS_COLORS[o.status] || "#888", background: `${STATUS_COLORS[o.status]}18`, textTransform: "capitalize" }}>{o.status}</span>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.shipping_address || "—"}</span>
          </div>
        ))}
        {orders.length === 0 && <p style={{ padding: "24px 22px", fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>No orders found.</p>}
      </div>
    </div>
  );
}
// ── MONTHLY REPORT ────────────────────────────────────────────────────────────
function MonthlyReport({ data }) {
  const { orders } = data;
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Get available years from order data
  const years = [...new Set(orders.map(o => new Date(o.created_at).getFullYear()))].sort((a,b) => b - a);
  if (!years.includes(currentYear)) years.unshift(currentYear);

  // Build monthly data for selected year
  const monthly = MONTHS.map((month, idx) => {
    const monthOrders = orders.filter(o => {
      const d = new Date(o.created_at);
      return d.getFullYear() === selectedYear && d.getMonth() === idx;
    });
    const revenue  = monthOrders.filter(o => o.status === "delivered").reduce((s, o) => s + parseFloat(o.total_price || 0), 0);
    const count    = monthOrders.length;
    const delivered = monthOrders.filter(o => o.status === "delivered").length;
    const cancelled = monthOrders.filter(o => o.status === "cancelled").length;
    return { month, revenue, count, delivered, cancelled };
  });

  const maxRevenue = Math.max(...monthly.map(m => m.revenue), 1);
  const maxCount   = Math.max(...monthly.map(m => m.count),   1);

  const yearTotal   = monthly.reduce((s, m) => s + m.revenue, 0);
  const yearOrders  = monthly.reduce((s, m) => s + m.count, 0);
  const bestMonth   = monthly.reduce((best, m) => m.revenue > best.revenue ? m : best, monthly[0]);
  const avgMonthly  = yearTotal / 12;

  return (
    <div>
      {/* Year selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "14px", fontWeight: "700", color: "#1a1a1a" }}>
          Monthly Report —
        </p>
        <div style={{ display: "flex", gap: "6px" }}>
          {years.map(y => (
            <button key={y} onClick={() => setSelectedYear(y)}
              style={{ padding: "6px 16px", borderRadius: "999px", border: "none", cursor: "pointer",
                fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: selectedYear === y ? "700" : "400",
                background: selectedYear === y ? "#e07b39" : "#f0ebe3",
                color: selectedYear === y ? "#fff" : "#888" }}>
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Year KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "20px" }}>
        {[
          { label: "Year Revenue (Delivered)", value: inr(yearTotal),    icon: "💰", color: "#3a8a68" },
          { label: "Total Orders",   value: yearOrders,        icon: "📦", color: "#e07b39" },
          { label: "Best Month",     value: bestMonth.revenue > 0 ? bestMonth.month : "—", icon: "🏆", color: "#c4a020" },
          { label: "Monthly Avg",    value: inr(avgMonthly),   icon: "📊", color: "#3a6ab0" },
        ].map(({ label, value, icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: "14px", padding: "20px", border: "1px solid #f0ebe3" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
              <span style={{ fontSize: "20px" }}>{icon}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</span>
            </div>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "20px", fontWeight: "700", color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Revenue bar chart */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", padding: "22px", marginBottom: "20px" }}>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a", marginBottom: "20px" }}>
          Monthly Revenue — {selectedYear}
        </p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", height: "180px", paddingBottom: "0" }}>
          {monthly.map((m, i) => {
            const heightPct = maxRevenue > 0 ? (m.revenue / maxRevenue) * 100 : 0;
            const isCurrentMonth = selectedYear === currentYear && i === new Date().getMonth();
            return (
              <div key={m.month} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", height: "100%", justifyContent: "flex-end" }}>
                {m.revenue > 0 && (
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#aaa", whiteSpace: "nowrap" }}>
                    {inr(m.revenue)}
                  </span>
                )}
                <div style={{ width: "100%", height: `${heightPct}%`, minHeight: m.revenue > 0 ? "4px" : "0",
                  background: isCurrentMonth ? "linear-gradient(180deg,#e07b39,#c96a18)" : m.revenue > 0 ? "linear-gradient(180deg,#f0c89a,#e0a87a)" : "#f7f2eb",
                  borderRadius: "6px 6px 0 0", transition: "height 0.5s ease",
                  border: isCurrentMonth ? "none" : "none" }}
                  title={`${m.month}: ${inr(m.revenue)} · ${m.count} orders`}
                />
                <span style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: isCurrentMonth ? "#e07b39" : "#aaa", fontWeight: isCurrentMonth ? "700" : "400" }}>
                  {m.month}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly table */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #f0ebe3" }}>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#1a1a1a" }}>
            Month-wise Breakdown — {selectedYear}
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 100px 100px 100px 100px", gap: "8px", padding: "10px 22px", background: "#f7f2eb" }}>
          {["MONTH", "REVENUE BAR", "REVENUE", "ORDERS", "DELIVERED", "CANCELLED"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>
        {monthly.map((m, i) => {
          const isCurrentMonth = selectedYear === currentYear && i === new Date().getMonth();
          const pct = maxCount > 0 ? Math.round((m.count / maxCount) * 100) : 0;
          return (
            <div key={m.month} style={{ display: "grid", gridTemplateColumns: "100px 1fr 100px 100px 100px 100px", gap: "8px", padding: "12px 22px", borderBottom: "1px solid #f7f2eb", alignItems: "center", background: isCurrentMonth ? "#fffaf6" : "transparent" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: isCurrentMonth ? "700" : "600", color: isCurrentMonth ? "#e07b39" : "#1a1a1a" }}>
                {m.month} {isCurrentMonth && <span style={{ fontSize: "9px", background: "#e07b39", color: "#fff", borderRadius: "999px", padding: "1px 6px", marginLeft: "4px" }}>NOW</span>}
              </span>
              <div style={{ height: "8px", borderRadius: "999px", background: "#f0ebe3", overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg,#e07b39,#c96a18)", borderRadius: "999px", transition: "width 0.5s" }} />
              </div>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: m.revenue > 0 ? "#3a8a68" : "#aaa" }}>
                {m.revenue > 0 ? inr(m.revenue) : "—"}
              </span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: m.count > 0 ? "#e07b39" : "#aaa", textAlign: "center" }}>{m.count || "—"}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: m.delivered > 0 ? "#3a8a68" : "#aaa", textAlign: "center" }}>{m.delivered || "—"}</span>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: m.cancelled > 0 ? "#c0392b" : "#aaa", textAlign: "center" }}>{m.cancelled || "—"}</span>
            </div>
          );
        })}
        <div style={{ padding: "14px 22px", background: "#faf7f2", display: "grid", gridTemplateColumns: "100px 1fr 100px 100px 100px 100px", gap: "8px", alignItems: "center" }}>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", fontWeight: "700", color: "#1a1a1a" }}>TOTAL</span>
          <span />
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#3a8a68" }}>{inr(yearTotal)}</span>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#e07b39", textAlign: "center" }}>{yearOrders}</span>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#3a8a68", textAlign: "center" }}>
            {monthly.reduce((s,m) => s + m.delivered, 0) || "—"}
          </span>
          <span style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700", color: "#c0392b", textAlign: "center" }}>
            {monthly.reduce((s,m) => s + m.cancelled, 0) || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}